import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import data from './wsetWines.json';
import RatingLookup from './RatingLookup';
import './WsetCourse.css';

const STORAGE_KEY = 'kosmoholm.wset.tasted';
const TIER_LABEL = { budget: 'Budget', mid: 'Mid', premium: 'Premium' };

function loadTasted() {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

const kr = value => `${Math.round(value).toLocaleString('nb-NO')} kr`;

function Pick({ pick, t }) {
  return (
    <a className={`wset-pick wset-pick-${pick.tier}`} href={pick.url} target="_blank" rel="noreferrer">
      <span className="wset-pick-tier">
        {t(`grapeEscape.wset.tier.${pick.tier}`, TIER_LABEL[pick.tier])}
      </span>
      <span className="wset-pick-name">{pick.name}</span>
      <span className="wset-pick-meta">
        {[pick.country, pick.region].filter(Boolean).join(' · ')}
      </span>
      <span className="wset-pick-price">{kr(pick.priceNOK)}</span>
    </a>
  );
}

function Item({ item, tasted, onToggle, t }) {
  const [open, setOpen] = useState(false);
  const done = Boolean(tasted[item.id]);

  return (
    <li className={`wset-item ${done ? 'is-done' : ''}`}>
      <div className="wset-item-head">
        <label className="wset-check">
          <input type="checkbox" checked={done} onChange={() => onToggle(item.id)} />
          <span className="sr-only">
            {t('grapeEscape.wset.markTasted', 'Mark as tasted')}
          </span>
        </label>
        <button type="button" className="wset-item-title" onClick={() => setOpen(o => !o)} aria-expanded={open}>
          <span className="wset-item-name">{item.title}</span>
          {item.grape && <span className="wset-grape">{item.grape}</span>}
        </button>
        <span className="wset-item-price">
          {item.picks.length > 0
            ? `${kr(item.picks[0].priceNOK)} – ${kr(item.picks[item.picks.length - 1].priceNOK)}`
            : t('grapeEscape.wset.noPicks', 'Nothing in stock')}
        </span>
      </div>

      {open && (
        <div className="wset-item-body">
          <p className="wset-requirement">
            <span className="wset-label">{t('grapeEscape.wset.requirement', 'Specification')}</span>
            {item.requirement}
          </p>
          <p className="wset-lookfor">
            <span className="wset-label">{t('grapeEscape.wset.lookFor', 'What to taste for')}</span>
            {item.lookFor}
          </p>
          {item.picks.length > 0 ? (
            <div className="wset-picks">
              {item.picks.map(pick => (
                <div key={pick.code} className="wset-pick-wrap">
                  <Pick pick={pick} t={t} />
                  <RatingLookup
                    name={pick.name}
                    bordeaux={/bordeaux|medoc|médoc|pauillac|margaux|saint-[ée]milion|pomerol|graves|sauternes/i.test(
                      `${pick.region || ''} ${pick.name || ''}`
                    )}
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="wset-gap">
              {t(
                'grapeEscape.wset.gap',
                'Vinmonopolet has nothing matching this right now. Ask the shop to order, or substitute a close style.'
              )}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

function WsetCourse() {
  const { t } = useTranslation();
  const [levelId, setLevelId] = useState(data.levels[0].id);
  const [tasted, setTasted] = useState(loadTasted);

  const level = data.levels.find(l => l.id === levelId) || data.levels[0];

  const toggle = id => {
    setTasted(prev => {
      const next = { ...prev, [id]: !prev[id] };
      if (!next[id]) delete next[id];
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* storage may be unavailable in private mode */
      }
      return next;
    });
  };

  const { total, done, cheapest } = useMemo(() => {
    const items = level.sessions.flatMap(s => s.items);
    return {
      total: items.length,
      done: items.filter(i => tasted[i.id]).length,
      cheapest: items.reduce(
        (sum, i) => sum + (i.picks.length ? i.picks[0].priceNOK : 0),
        0
      ),
    };
  }, [level, tasted]);

  return (
    <div className="wset-course">
      <header className="wset-header">
        <h2>{level.title}</h2>
        <p className="wset-sub">{level.subtitle}</p>
      </header>

      {data.levels.length > 1 && (
        <div className="wset-levels" role="tablist">
          {data.levels.map(l => (
            <button
              key={l.id}
              role="tab"
              aria-selected={l.id === levelId}
              className={`wset-level ${l.id === levelId ? 'active' : ''}`}
              onClick={() => setLevelId(l.id)}
            >
              {l.title}
            </button>
          ))}
        </div>
      )}

      <div className="wset-summary">
        <div>
          <strong>{done} / {total}</strong>
          <span>{t('grapeEscape.wset.tastedCount', 'wines tasted')}</span>
        </div>
        <div>
          <strong>{kr(cheapest)}</strong>
          <span>{t('grapeEscape.wset.budgetTotal', 'full set, budget picks')}</span>
        </div>
        <div>
          <strong>{data.generatedAt}</strong>
          <span>{t('grapeEscape.wset.checked', 'prices checked')}</span>
        </div>
      </div>

      <div className="wset-sessions">
        {level.sessions.map(session => (
          <section key={session.id} className="wset-session">
            <h3>
              {session.title}
              <span className="wset-theme">{session.theme}</span>
            </h3>
            <ul className="wset-items">
              {session.items.map(item => (
                <Item key={item.id} item={item} tasted={tasted} onToggle={toggle} t={t} />
              ))}
            </ul>
          </section>
        ))}
      </div>

      <p className="wset-footnote">
        {t(
          'grapeEscape.wset.footnote',
          'Three price points per wine, all orderable from Vinmonopolet when the list was built. Prices and stock change, so check before you order.'
        )}
      </p>
    </div>
  );
}

export default WsetCourse;
